const getParentIndex = (index: number): number => Math.floor((index - 1) / 2);
const getLeftChildIndex = (index: number): number => 2 * index + 1;
const getRightChildIndex = (index: number): number => 2 * index + 2;

type HeapNode<T> = {
  value: T;
  priority: number;
};

const defaultHash = <T>(value: T): T => value;

/**
 * Min-heap based priority queue with O(log n) insert/delete and O(1) lookup.
 * Lower priority values are dequeued first.
 *
 * Implemented as a class by symmetry with the native collections.
 */
export class PriorityQueue<T> {
  private readonly heap: HeapNode<T>[] = [];
  private readonly indexMap = new Map<unknown, number>();
  private readonly hashFn: (value: T) => unknown;

  public constructor(hashFn: (value: T) => unknown = defaultHash) {
    this.hashFn = hashFn;
  }

  public get size(): number {
    return this.heap.length;
  }

  /**
   * Returns the node with the lowest priority without removing it.
   */
  public peek(): HeapNode<T> | undefined {
    return this.heap[0];
  }

  /**
   * Removes and returns the node with the lowest priority.
   */
  public pop(): HeapNode<T> | undefined {
    if (this.heap.length === 0) {
      return undefined;
    }

    const result = this.heap[0];
    this.indexMap.delete(this.hashFn(result.value));

    const last = this.heap.pop()!; // pop cannot return nullish in this context but linter doesn't know

    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.indexMap.set(this.hashFn(last.value), 0);
      this.bubbleDown(0);
    }

    return result;
  }

  /**
   * Checks if a value exists in the queue.
   */
  public has(value: T): boolean {
    return this.indexMap.has(this.hashFn(value));
  }

  /**
   * Adds a new value with the given priority to the queue.
   * The value must not already exist in the queue; adding a duplicate would
   * corrupt the heap (the indexMap entry would be overwritten, orphaning the
   * earlier node). Use {@link set} when the value may already be present.
   */
  public add(value: T, priority: number): void {
    if (this.has(value)) {
      throw new Error(
        'PriorityQueue.add called with a value already in the queue; use set() instead'
      );
    }

    const node: HeapNode<T> = { value, priority };
    const index = this.heap.length;

    this.heap.push(node);
    this.indexMap.set(this.hashFn(value), index);
    this.bubbleUp(index);
  }

  /**
   * Updates the priority of an existing value, or adds it if not present.
   * If the value exists, updates its priority and maintains heap property.
   */
  public set(value: T, priority: number): void {
    const hash = this.hashFn(value);
    const index = this.indexMap.get(hash);

    if (index === undefined) {
      this.add(value, priority);
      return;
    }

    const oldPriority = this.heap[index].priority;

    if (oldPriority === priority) {
      return;
    }

    this.heap[index].priority = priority;

    if (priority < oldPriority) {
      this.bubbleUp(index);
    } else {
      this.bubbleDown(index);
    }
  }

  private swap(i: number, j: number): void {
    const nodeI = this.heap[i];
    const nodeJ = this.heap[j];

    this.indexMap.set(this.hashFn(nodeI.value), j);
    this.indexMap.set(this.hashFn(nodeJ.value), i);

    this.heap[i] = nodeJ;
    this.heap[j] = nodeI;
  }

  private bubbleUp(startIndex: number): void {
    let index = startIndex;

    while (index > 0) {
      const parentIndex = getParentIndex(index);

      if (this.heap[index].priority >= this.heap[parentIndex].priority) {
        break;
      }

      this.swap(index, parentIndex);
      index = parentIndex;
    }
  }

  private bubbleDown(startIndex: number): void {
    let index = startIndex;
    const length = this.heap.length;

    while (true) {
      const leftChildIndex = getLeftChildIndex(index);
      const rightChildIndex = getRightChildIndex(index);

      if (leftChildIndex >= length) {
        break;
      }

      const smallestChildIndex =
        rightChildIndex >= length ||
        this.heap[leftChildIndex].priority < this.heap[rightChildIndex].priority
          ? leftChildIndex
          : rightChildIndex;

      if (this.heap[index].priority <= this.heap[smallestChildIndex].priority) {
        break;
      }

      this.swap(index, smallestChildIndex);
      index = smallestChildIndex;
    }
  }
}
